import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchVoiceListeners, Listener } from '../../api/listenersApi';
import {
  InternalClassicReviewDetail,
  InternalClassicReviewListItem,
  completeInternalClassicReview,
  enqueueInternalClassicReview,
  fetchInternalClassicReview,
  fetchInternalClassicReviews,
  makeInternalClassicReviewAvailable,
} from '../../api/rendersApi';
import { useApp } from '../../context/AppContext';

interface ListeningReviewWorkspaceProps {
  storyId: string;
  title: string;
  storyPublished: boolean;
  onReviewChange: () => Promise<void>;
}

const reviewableStatuses = new Set(['machine_passed', 'needs_review']);
const pendingStatuses = new Set(['queued', 'rendering', 'regenerating']);

function newestRendersByListener(
  renders: InternalClassicReviewListItem[],
  storyId: string,
  voiceId: string
): Map<string, InternalClassicReviewListItem> {
  const result = new Map<string, InternalClassicReviewListItem>();
  renders
    .filter((render) => render.story_id === storyId && render.voice_id === voiceId && render.listener_id)
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .forEach((render) => {
      if (render.listener_id && !result.has(render.listener_id)) result.set(render.listener_id, render);
    });
  return result;
}

function replacementRenderHasStarted(
  render: InternalClassicReviewListItem,
  storyId: string,
  voiceId: string,
  listenerId: string
): boolean {
  return render.story_id === storyId
    && render.voice_id === voiceId
    && render.listener_id === listenerId
    && render.status !== 'failed';
}

const InternalListenPlayer: React.FC<{
  render: InternalClassicReviewListItem;
  onApproved: () => Promise<void>;
}> = ({ render, onApproved }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const lastPlaybackPositionRef = useRef<number | null>(null);
  const seekingRef = useRef(false);
  const [detail, setDetail] = useState<InternalClassicReviewDetail | null>(null);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [listenedSeconds, setListenedSeconds] = useState(0);
  const [sampleSeconds, setSampleSeconds] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setDetail(null);
    setError('');
    setListenedSeconds(0);
    setSampleSeconds({});
    lastPlaybackPositionRef.current = null;
    void fetchInternalClassicReview(render.render_id)
      .then(setDetail)
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load review audio'));
  }, [render.render_id]);

  if (error) return <p className="listening-review__error">{error}</p>;
  if (!detail) return <p>Loading private review audio…</p>;

  const runtimeSeconds = detail.output_seconds || durationSeconds;
  const reviewSamples = detail.review_samples || [];
  const completedSampleIds = reviewSamples
    .filter((sample) => (sampleSeconds[sample.sample_id] || 0) >= sample.required_seconds)
    .map((sample) => sample.sample_id);
  const approvalReady = reviewSamples.length === 5
    && completedSampleIds.length === reviewSamples.length;

  const recordProgress = (audio: HTMLAudioElement) => {
    const current = audio.currentTime;
    const previous = lastPlaybackPositionRef.current;
    if (!seekingRef.current && !audio.paused && previous !== null) {
      const delta = current - previous;
      if (delta > 0 && delta <= 5) {
        setListenedSeconds((seconds) => seconds + delta);
        setSampleSeconds((progress) => {
          const next = { ...progress };
          reviewSamples.forEach((sample) => {
            const overlap = Math.max(
              0,
              Math.min(current, sample.end_seconds)
                - Math.max(previous, sample.start_seconds)
            );
            if (overlap > 0) {
              next[sample.sample_id] = Math.min(
                sample.required_seconds,
                (next[sample.sample_id] || 0) + overlap
              );
            }
          });
          return next;
        });
      }
    }
    lastPlaybackPositionRef.current = current;
  };

  const approve = async () => {
    if (!approvalReady) return;
    setBusy(true);
    setError('');
    try {
      await completeInternalClassicReview(render.render_id, {
        evidence_version: 2,
        listened_seconds: listenedSeconds,
        completion_ratio: runtimeSeconds > 0 ? Math.min(1, listenedSeconds / runtimeSeconds) : 0,
        intro_listened: completedSampleIds.includes('intro'),
        goodnight_listened: completedSampleIds.includes('goodnight'),
        reviewed_sample_ids: completedSampleIds,
      });
      await onApproved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not approve this listening review');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="listening-review__player">
      <audio
        ref={audioRef}
        controls
        src={detail.audio_url ?? undefined}
        preload="metadata"
        onPlay={(event) => { lastPlaybackPositionRef.current = event.currentTarget.currentTime; }}
        onPause={() => { lastPlaybackPositionRef.current = null; }}
        onLoadedMetadata={(event) => setDurationSeconds(event.currentTarget.duration)}
        onTimeUpdate={(event) => {
          const audio = event.currentTarget;
          recordProgress(audio);
        }}
        onSeeking={() => { seekingRef.current = true; }}
        onSeeked={(event) => {
          lastPlaybackPositionRef.current = event.currentTarget.currentTime;
          seekingRef.current = false;
        }}
        onEnded={(event) => { recordProgress(event.currentTarget); lastPlaybackPositionRef.current = null; }}
      >
        Your browser does not support audio playback.
      </audio>
      <div className="listening-review__samples" aria-live="polite">
        {reviewSamples.map((sample) => {
          const complete = completedSampleIds.includes(sample.sample_id);
          const heard = Math.min(sample.required_seconds, sampleSeconds[sample.sample_id] || 0);
          return <button
            type="button"
            key={sample.sample_id}
            className={complete ? 'complete' : ''}
            onClick={() => {
              const audio = audioRef.current;
              if (!audio) return;
              audio.currentTime = sample.start_seconds;
              lastPlaybackPositionRef.current = sample.start_seconds;
              void audio.play();
            }}
          >
            <span>{complete ? '✓' : '▶'} {sample.label}</span>
            <small>{complete ? 'heard' : `${Math.ceil(Math.max(0, sample.required_seconds - heard))} sec left`}</small>
          </button>;
        })}
      </div>
      <button className="primary" disabled={busy || !approvalReady} onClick={() => void approve()}>
        {busy ? 'Approving…' : approvalReady ? 'Approve this private review' : 'Review all 5 samples'}
      </button>
      <p className="listening-review__privacy">Review the two personal bookends plus opening, middle, and closing story samples. Approval records editorial evidence only; this audio remains outside the family library.</p>
    </div>
  );
};

const ListeningReviewWorkspace: React.FC<ListeningReviewWorkspaceProps> = ({
  storyId,
  title,
  storyPublished,
  onReviewChange,
}) => {
  const { state, fetchVoices } = useApp();
  const [voiceId, setVoiceId] = useState('');
  const [listeners, setListeners] = useState<Listener[]>([]);
  const [selectedListenerIds, setSelectedListenerIds] = useState<string[]>([]);
  const [renders, setRenders] = useState<InternalClassicReviewListItem[]>([]);
  const [queuedListenerIds, setQueuedListenerIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [promotingRenderId, setPromotingRenderId] = useState('');
  const [availableRenderIds, setAvailableRenderIds] = useState<string[]>([]);

  const refreshRenders = useCallback(async () => {
    const next = await fetchInternalClassicReviews();
    setRenders(next);
    setQueuedListenerIds((queued) => queued.filter(
      (listenerId) => !next.some((render) =>
        replacementRenderHasStarted(render, storyId, voiceId, listenerId)
      )
    ));
  }, [storyId, voiceId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchVoices(), fetchInternalClassicReviews()])
      .then(([, nextRenders]) => { setRenders(nextRenders); setError(''); })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load listening review'))
      .finally(() => setLoading(false));
  }, [fetchVoices, storyId]);

  useEffect(() => {
    if (!voiceId) {
      setListeners([]);
      setSelectedListenerIds([]);
      return;
    }
    setLoading(true);
    fetchVoiceListeners(voiceId)
      .then((next) => {
        setListeners(next.filter((listener) => listener.state === 'active'));
        setSelectedListenerIds([]);
        setError('');
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load listeners'))
      .finally(() => setLoading(false));
  }, [voiceId]);

  const rendersByListener = useMemo(
    () => newestRendersByListener(renders, storyId, voiceId),
    [renders, storyId, voiceId]
  );
  const selectedVoice = state.voices.find((voice) => voice.voice_id === voiceId);
  const selectedListeners = listeners.filter((listener) => selectedListenerIds.includes(listener.listener_id));
  const selectionsReady = selectedListeners.length === 2 && selectedListeners.every((listener) => listener.bookend_complete);
  const voiceReady = selectedVoice?.status === 'ready' && selectedVoice.has_cartesia;
  const shouldPoll = queuedListenerIds.length > 0 || Array.from(rendersByListener.values()).some((render) => pendingStatuses.has(render.status));

  useEffect(() => {
    if (!shouldPoll) return;
    const timer = window.setInterval(() => {
      void refreshRenders().catch((err) => setError(err instanceof Error ? err.message : 'Could not refresh renders'));
    }, 4000);
    return () => window.clearInterval(timer);
  }, [refreshRenders, shouldPoll]);

  const toggleListener = (listenerId: string) => {
    setSelectedListenerIds((current) => {
      if (current.includes(listenerId)) return current.filter((id) => id !== listenerId);
      if (current.length >= 2) return current;
      return [...current, listenerId];
    });
  };

  const createReviewRenders = async () => {
    if (!voiceReady || !selectionsReady) return;
    if (!window.confirm(`Create two private TTS review renders of ${title}? Provider usage may be incurred.`)) return;
    setBusy(true);
    setMessage('');
    setError('');
    try {
      const results = await Promise.all(selectedListenerIds.map((listenerId) =>
        enqueueInternalClassicReview(storyId, voiceId, listenerId)
      ));
      const queued = selectedListenerIds.filter((_, index) => results[index].status === 'queued');
      setQueuedListenerIds((current) => Array.from(new Set([...current, ...queued])));
      setMessage(queued.length ? 'Two private review renders were queued.' : 'The selected review renders already exist.');
      await refreshRenders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create private review renders');
    } finally {
      setBusy(false);
    }
  };

  const reviewedCount = Array.from(rendersByListener.values()).filter((render) => render.status === 'internal_reviewed').length;

  const makeAvailable = async (render: InternalClassicReviewListItem) => {
    setPromotingRenderId(render.render_id);
    setMessage('');
    setError('');
    try {
      await makeInternalClassicReviewAvailable(render.render_id);
      setAvailableRenderIds((current) => Array.from(new Set([...current, render.render_id])));
      setMessage('The approved private audio is now available in Tonight. No audio was regenerated.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not make this audio available');
    } finally {
      setPromotingRenderId('');
    }
  };

  return (
    <div className="listening-review" data-testid="listening-review-workspace">
      <div className="listening-review__heading">
        <div>
          <h3>Private listening workspace</h3>
          <p>Choose one Family voice and exactly two stable listeners. Each listener needs an active introduction and goodnight for that voice.</p>
        </div>
        <span className={`admin-status ${reviewedCount >= 2 ? 'admin-status--complete' : 'admin-status--queued'}`}>{reviewedCount}/2 reviewed</span>
      </div>

      {message && <p className="classics-admin__notice classics-admin__notice--success">{message}</p>}
      {error && <p className="classics-admin__notice classics-admin__notice--error">{error}</p>}

      <label className="editor-label">
        Family voice
        <select value={voiceId} onChange={(event) => setVoiceId(event.target.value)}>
          <option value="">Choose a voice</option>
          {state.voices.map((voice) => (
            <option key={voice.voice_id} value={voice.voice_id} disabled={voice.status !== 'ready' || !voice.has_cartesia}>
              {voice.voice_name}{voice.status === 'ready' && voice.has_cartesia ? '' : ' — not render-ready'}
            </option>
          ))}
        </select>
      </label>

      {voiceId && <div className="listening-review__listeners">
        {listeners.map((listener) => {
          const render = rendersByListener.get(listener.listener_id);
          const selected = selectedListenerIds.includes(listener.listener_id);
          const queued = queuedListenerIds.includes(listener.listener_id);
          return (
            <article key={listener.listener_id} className={selected ? 'selected' : ''}>
              <label>
                <input
                  type="checkbox"
                  checked={selected}
                  disabled={!listener.bookend_complete || (!selected && selectedListenerIds.length >= 2)}
                  onChange={() => toggleListener(listener.listener_id)}
                />
                <span><strong>{listener.display_name}</strong><small>{listener.bookend_complete ? 'Bookends ready' : 'Bookends incomplete'}</small></span>
              </label>
              <div className="listening-review__bookends" aria-label={`${listener.display_name} bookends`}>
                <span className={listener.bookends?.intro ? 'complete' : ''}>{listener.bookends?.intro ? '✓' : '○'} Introduction</span>
                <span className={listener.bookends?.goodnight ? 'complete' : ''}>{listener.bookends?.goodnight ? '✓' : '○'} Goodnight</span>
              </div>
              {render && <span className={`admin-pill ${render.status === 'internal_reviewed' ? 'admin-status--complete' : ''}`}>{render.status.replace(/_/g, ' ')}</span>}
              {!render && queued && <span className="admin-pill admin-status--queued">queued</span>}
              {!listener.bookend_complete && <Link to="/voice" state={{ invitedVoiceId: voiceId, invitedVoiceName: selectedVoice?.voice_name, invitedListenerId: listener.listener_id, invitedListenerName: listener.display_name }}>Record missing clips →</Link>}
            </article>
          );
        })}
        {!loading && listeners.length === 0 && <p>No active listeners are available for this voice.</p>}
      </div>}

      <div className="listening-review__enqueue">
        <button className="primary" disabled={busy || !voiceReady || !selectionsReady} onClick={() => void createReviewRenders()}>
          {busy ? 'Creating private renders…' : 'Create 2 private review renders'}
        </button>
        <span>{selectedListenerIds.length}/2 listeners selected. This action uses the configured TTS provider but does not publish or expose the audio.</span>
      </div>

      {Array.from(rendersByListener.values()).map((render) => (
        <article className="listening-review__render" key={render.render_id}>
          <header>
            <div><strong>{listeners.find((listener) => listener.listener_id === render.listener_id)?.display_name || 'Listener'} review</strong><span>{render.status.replace(/_/g, ' ')}</span></div>
            {render.output_seconds ? <span>{Math.ceil(render.output_seconds / 60)} min audio</span> : null}
          </header>
          {render.status === 'internal_reviewed' && <p className="all-clear">✓ Private listening review complete for this listener.</p>}
          {render.status === 'internal_reviewed' && storyPublished && <button
            className="primary"
            disabled={!!promotingRenderId || availableRenderIds.includes(render.render_id)}
            onClick={() => void makeAvailable(render)}
          >
            {availableRenderIds.includes(render.render_id)
              ? 'Available tonight'
              : promotingRenderId === render.render_id
                ? 'Making available…'
                : 'Make available tonight'}
          </button>}
          {pendingStatuses.has(render.status) && <p>Rendering and quality checks are running. This page refreshes automatically.</p>}
          {render.status === 'failed' && <p className="listening-review__error">This render failed. Select this listener above and create the review renders again.</p>}
          {reviewableStatuses.has(render.status) && <InternalListenPlayer render={render} onApproved={async () => { await refreshRenders(); await onReviewChange(); }} />}
        </article>
      ))}
    </div>
  );
};

export { newestRendersByListener, replacementRenderHasStarted };
export default ListeningReviewWorkspace;
